import React from "react";
import styles from "./jsaPpeInfoComponents.module.css";

// Helper function to get field error for PPE fields
const getPpeFieldError = (autosaveErrors, fieldName) => {
  const key = `jsa_ppe.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[key] || null;
};

const JsaPpeCheckbox = ({ callbacks, label, ppeType, value }) => {
  // Handling the checkbox change
  const handleCheckboxChange = (e) => {
    callbacks.v1Actions.updateJsaPpe(
      callbacks.jsaData,
      ppeType,
      e.target.checked,
      callbacks.emitPatch,
    );
  };

  return (
    <div className={styles.jsaPpeRowInput}>
      <label className={styles.jsaPpeRowLabel}>{label}</label>
      <input
        type="checkbox"
        checked={value}
        onChange={handleCheckboxChange}
        className={styles.jsaPpeRowInputCheckbox}
        disabled={callbacks.jsaMode === "view"}
      />
    </div>
  );
};

const JsaPpeOtherInput = ({
  callbacks,
  label,
  ppeType,
  value,
  placeholder,
  fieldName, // Field name for error lookup
}) => {
  // Get autosave error for this field
  const fieldError = fieldName
    ? getPpeFieldError(callbacks.autosaveErrors, fieldName)
    : null;

  // Handling the text input change (local state only on onChange)
  const handleTextInputChange = (e) => {
    callbacks.v1Actions.updateJsaPpe(
      callbacks.jsaData,
      ppeType,
      e.target.value,
      null, // Don't emit on every keystroke
    );
  };

  // Handle blur - send patch to server
  const handleBlur = (e) => {
    callbacks.v1Actions.updateJsaPpe(
      callbacks.jsaData,
      ppeType,
      e.target.value,
      callbacks.emitPatch, // Emit on blur
    );
  };

  return (
    <div className={styles.jsaPpeRowInput}>
      <label className={styles.jsaPpeRowLabel}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={handleTextInputChange}
        onBlur={handleBlur}
        className={`${styles.jsaPpeRowInputText} ${fieldError ? styles.inputError : ""}`}
        placeholder={placeholder}
        disabled={callbacks.jsaMode === "view"}
      />
      {fieldError && <span className={styles.errorText}>{fieldError}</span>}
    </div>
  );
};

// Setting up individual PPE section components, Eye and Face, Hearing adn Head, Hand, Foot, Protective Clothing, Respiratory Protection
const JsaPpeEyeAndFace = ({ callbacks }) => {
  return (
    <div className={styles.jsaPpeSectionContainer}>
      <div className={styles.jsaPpeSectionTitle}>Eye and Face</div>
      <div className={styles.jsaPpeRow}>
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Safety Glasses"
          ppeType="ppeEyeFaceSafetyGlasses"
          value={callbacks.jsaData.jsaPpeInfo.ppeEyeFaceSafetyGlasses}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Goggles"
          ppeType="ppeEyeFaceGoggles"
          value={callbacks.jsaData.jsaPpeInfo.ppeEyeFaceGoggles}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Face Shield"
          ppeType="ppeEyeFaceFaceShield"
          value={callbacks.jsaData.jsaPpeInfo.ppeEyeFaceFaceShield}
        />
      </div>
    </div>
  );
};

export const JsaPpeHearingAndHead = ({ callbacks }) => {
  return (
    <div className={styles.jsaPpeSectionContainer}>
      <div className={styles.jsaPpeSectionTitle}>Hearing and Head</div>
      <div className={styles.jsaPpeRow}>
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Ear Plugs"
          ppeType="ppeHearingEarPlugs"
          value={callbacks.jsaData.jsaPpeInfo.ppeHearingEarPlugs}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Ear Muffs"
          ppeType="ppeHearingEarMuffs"
          value={callbacks.jsaData.jsaPpeInfo.ppeHearingEarMuffs}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Hard Hat"
          ppeType="ppeHeadHardHat"
          value={callbacks.jsaData.jsaPpeInfo.ppeHeadHardHat}
        />
      </div>
    </div>
  );
};

export const JsaPpeHand = ({ callbacks }) => {
  return (
    <div className={styles.jsaPpeSectionContainer}>
      <div className={styles.jsaPpeSectionTitle}>Hand</div>
      <div className={styles.jsaPpeRow}>
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Leather"
          ppeType="ppeHandLeather"
          value={callbacks.jsaData.jsaPpeInfo.ppeHandLeather}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Cut Resistant"
          ppeType="ppeHandCutResistant"
          value={callbacks.jsaData.jsaPpeInfo.ppeHandCutResistant}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Chemical Resistant"
          ppeType="ppeHandChemicalResistant"
          value={callbacks.jsaData.jsaPpeInfo.ppeHandChemicalResistant}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Other"
          ppeType="ppeHandOther"
          value={callbacks.jsaData.jsaPpeInfo.ppeHandOther}
        />
      </div>
      {(callbacks.jsaV1.ppe.showJsaPpeHandOtherSection ||
        callbacks.jsaData.jsaPpeInfo.ppeHandOther === true ||
        callbacks.jsaData.jsaPpeInfo.ppeHandOtherInfo !== "") && (
        <JsaPpeOtherInput
          callbacks={callbacks}
          label="Other"
          ppeType="ppeHandOtherInfo"
          value={callbacks.jsaData.jsaPpeInfo.ppeHandOtherInfo}
          placeholder="Specify other hand PPE"
          fieldName="ppe_hand_other_info"
        />
      )}
    </div>
  );
};

export const JsaPpeFoot = ({ callbacks }) => {
  return (
    <div className={styles.jsaPpeSectionContainer}>
      <div className={styles.jsaPpeSectionTitle}>Foot</div>
      <div className={styles.jsaPpeRow}>
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Steel Toe"
          ppeType="ppeFootSteelToe"
          value={callbacks.jsaData.jsaPpeInfo.ppeFootSteelToe}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Metatarsal"
          ppeType="ppeFootMetatarsal"
          value={callbacks.jsaData.jsaPpeInfo.ppeFootMetatarsal}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Other"
          ppeType="ppeFootOther"
          value={callbacks.jsaData.jsaPpeInfo.ppeFootOther}
        />
      </div>
      {(callbacks.jsaV1.ppe.showJsaPpeFootOtherSection ||
        callbacks.jsaData.jsaPpeInfo.ppeFootOther === true ||
        callbacks.jsaData.jsaPpeInfo.ppeFootOtherInfo !== "") && (
        <JsaPpeOtherInput
          callbacks={callbacks}
          label="Other"
          ppeType="ppeFootOtherInfo"
          value={callbacks.jsaData.jsaPpeInfo.ppeFootOtherInfo}
          placeholder="Specify other foot PPE"
          fieldName="ppe_foot_other_info"
        />
      )}
    </div>
  );
};

export const JsaPpeProtectiveClothing = ({ callbacks }) => {
  return (
    <div className={styles.jsaPpeSectionContainer}>
      <div className={styles.jsaPpeSectionTitle}>Protective Clothing</div>
      <div className={styles.jsaPpeRow}>
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Nomex"
          ppeType="ppeClothingNomex"
          value={callbacks.jsaData.jsaPpeInfo.ppeClothingNomex}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Tyvek"
          ppeType="ppeClothingTyvek"
          value={callbacks.jsaData.jsaPpeInfo.ppeClothingTyvek}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Slicker Suit"
          ppeType="ppeClothingSlickerSuit"
          value={callbacks.jsaData.jsaPpeInfo.ppeClothingSlickerSuit}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Acid Suit"
          ppeType="ppeClothingAcidSuit"
          value={callbacks.jsaData.jsaPpeInfo.ppeClothingAcidSuit}
        />
      </div>
    </div>
  );
};

export const JsaPpeRespiratoryProtection = ({ callbacks }) => {
  return (
    <div className={styles.jsaPpeSectionContainer}>
      <div className={styles.jsaPpeSectionTitle}>Respiratory Protection</div>
      <div className={styles.jsaPpeSectionSubtitle}>
        <p className={styles.jsaPpeSectionSubtitleText}>
          Will respiratory protection be required?
        </p>
      </div>
      <div className={styles.jsaPpeRow}>
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="Yes"
          ppeType="ppeRespiratoryProtectionYes"
          value={callbacks.jsaData.jsaPpeInfo.ppeRespiratoryProtectionYes}
        />
        <JsaPpeCheckbox
          callbacks={callbacks}
          label="No"
          ppeType="ppeRespiratoryProtectionNo"
          value={callbacks.jsaData.jsaPpeInfo.ppeRespiratoryProtectionNo}
        />
      </div>
      {callbacks.jsaData.jsaPpeInfo.ppeRespiratoryProtectionYes && (
        <>
          <div className={styles.jsaPpeSectionSubtitle}>
            <p className={styles.jsaPpeSectionSubtitleText}>
              What type of respirator will be used?
            </p>
          </div>
          <div className={styles.jsaPpeRow}>
            <JsaPpeCheckbox
              callbacks={callbacks}
              label="Half Face"
              ppeType="ppeRespiratorTypeHalfFace"
              value={callbacks.jsaData.jsaPpeInfo.ppeRespiratorTypeHalfFace}
            />
            <JsaPpeCheckbox
              callbacks={callbacks}
              label="Full Face"
              ppeType="ppeRespiratorTypeFullFace"
              value={callbacks.jsaData.jsaPpeInfo.ppeRespiratorTypeFullFace}
            />
            <JsaPpeCheckbox
              callbacks={callbacks}
              label="Cartridge"
              ppeType="ppeRespiratorTypeCartridge"
              value={callbacks.jsaData.jsaPpeInfo.ppeRespiratorTypeCartridge}
            />
            <JsaPpeCheckbox
              callbacks={callbacks}
              label="SCBA"
              ppeType="ppeRespiratorTypeScba"
              value={callbacks.jsaData.jsaPpeInfo.ppeRespiratorTypeScba}
            />
            <JsaPpeCheckbox
              callbacks={callbacks}
              label="Supplied Air"
              ppeType="ppeRespiratorTypeSuppliedAir"
              value={callbacks.jsaData.jsaPpeInfo.ppeRespiratorTypeSuppliedAir}
            />
          </div>
        </>
      )}
    </div>
  );
};

export const JsaPpeComments = ({ callbacks }) => {
  // Get autosave error for comments
  const commentsError = getPpeFieldError(
    callbacks.autosaveErrors,
    "ppe_comments",
  );

  // Handling the comments change (local state only on onChange)
  const handleCommentsChange = (e) => {
    callbacks.v1Actions.updateJsaPpe(
      callbacks.jsaData,
      "ppeComments",
      e.target.value,
      null, // Don't emit on every keystroke
    );
  };

  // Handle blur - send patch to server
  const handleCommentsBlur = (e) => {
    callbacks.v1Actions.updateJsaPpe(
      callbacks.jsaData,
      "ppeComments",
      e.target.value,
      callbacks.emitPatch, // Emit on blur
    );
  };

  return (
    <div className={styles.jsaPpeSectionContainer}>
      <div className={styles.jsaPpeSectionTitle}>Comments</div>
      <div className={styles.jsaPpeSectionSubtitle}>
        <p className={styles.jsaPpeSectionSubtitleText}>
          Please provide any additional comments about the PPE requirements.
        </p>
      </div>
      <div className={styles.jsaPpeRow}>
        <textarea
          value={callbacks.jsaData.jsaPpeInfo.ppeComments}
          onChange={handleCommentsChange}
          onBlur={handleCommentsBlur}
          className={`${styles.jsaPpeRowInputText} ${commentsError ? styles.textareaError : ""}`}
          placeholder="Additional PPE comments"
          disabled={callbacks.jsaMode === "view"}
        />
        {commentsError && (
          <span className={styles.errorText}>{commentsError}</span>
        )}
      </div>
    </div>
  );
};

export const JsaPpeInfoTab = ({ callbacks }) => {
  // console.log("callbacks", callbacks);

  return (
    <div className={styles.jsaPpeInfoContainer}>
      <JsaPpeEyeAndFace callbacks={callbacks} />
      <JsaPpeHearingAndHead callbacks={callbacks} />
      <JsaPpeHand callbacks={callbacks} />
      <JsaPpeFoot callbacks={callbacks} />
      <JsaPpeProtectiveClothing callbacks={callbacks} />
      <JsaPpeRespiratoryProtection callbacks={callbacks} />
      <JsaPpeComments callbacks={callbacks} />
    </div>
  );
};
